import { Amplify } from 'aws-amplify';
import {
  fetchAuthSession,
  getCurrentUser as getAmplifyCurrentUser,
  setUpTOTP,
  signInWithRedirect,
  signOut,
  updateMFAPreference,
  verifyTOTPSetup,
} from 'aws-amplify/auth';

export type AuthUser = {
  username: string;
  userId: string;
  signInDetails?: unknown;
};

export type TotpSetup = {
  sharedSecret: string;
  setupUri: string;
};

export class AuthSessionExpiredError extends Error {
  constructor(message = 'Authentication session expired') {
    super(message);
    this.name = 'AuthSessionExpiredError';
  }
}

const region = process.env.REACT_APP_AWS_REGION || 'ap-northeast-1';
const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
const userPoolClientId = process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID;
const authDomain = process.env.REACT_APP_COGNITO_DOMAIN;
const redirectSignIn = process.env.REACT_APP_COGNITO_REDIRECT_SIGN_IN || window.location.origin;
const redirectSignOut = process.env.REACT_APP_COGNITO_REDIRECT_SIGN_OUT || window.location.origin;
const authReturnToKey = 'gib.auth.returnTo';
const manualLogoutKey = 'gib.auth.manualLogout';
const tokenExpiryBufferSeconds = 60;
let loginRedirectInProgress = false;
let refreshDepth = 0;
const refreshListeners = new Set<(refreshing: boolean) => void>();

export const isAuthConfigured = Boolean(userPoolId && userPoolClientId && authDomain);

if (isAuthConfigured) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: userPoolId as string,
        userPoolClientId: userPoolClientId as string,
        loginWith: {
          oauth: {
            domain: authDomain as string,
            scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
            redirectSignIn: [redirectSignIn],
            redirectSignOut: [redirectSignOut],
            responseType: 'code',
          },
        },
      },
    },
  });
}

const getReturnPath = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

const isTokenUsable = (token: { payload?: { exp?: unknown } } | undefined): boolean => {
  const exp = Number(token?.payload?.exp || 0);
  if (!exp) return false;
  return exp - Math.floor(Date.now() / 1000) > tokenExpiryBufferSeconds;
};

const fetchUsableIdToken = async (forceRefresh: boolean): Promise<string | null> => {
  const session = await fetchAuthSession({ forceRefresh });
  const idToken = session.tokens?.idToken;
  return isTokenUsable(idToken) ? idToken?.toString() || null : null;
};

const emitRefreshState = () => {
  const refreshing = refreshDepth > 0;
  refreshListeners.forEach((listener) => listener(refreshing));
};

const withRefreshNotification = async <T>(callback: () => Promise<T>): Promise<T> => {
  refreshDepth += 1;
  emitRefreshState();
  try {
    return await callback();
  } finally {
    refreshDepth = Math.max(0, refreshDepth - 1);
    emitRefreshState();
  }
};

export const subscribeAuthRefresh = (listener: (refreshing: boolean) => void): (() => void) => {
  refreshListeners.add(listener);
  listener(refreshDepth > 0);
  return () => {
    refreshListeners.delete(listener);
  };
};

export const isManualLogout = (): boolean => sessionStorage.getItem(manualLogoutKey) === 'true';

export const consumePostLoginRedirectPath = (): string | null => {
  const path = sessionStorage.getItem(authReturnToKey);
  if (path) {
    sessionStorage.removeItem(authReturnToKey);
  }
  return path;
};

export const login = async (): Promise<void> => {
  if (!isAuthConfigured || loginRedirectInProgress) return;
  loginRedirectInProgress = true;
  sessionStorage.removeItem(manualLogoutKey);
  sessionStorage.setItem(authReturnToKey, getReturnPath());
  await signInWithRedirect({ customState: getReturnPath() });
};

export const redirectToLogin = async (): Promise<never> => {
  await login();
  throw new AuthSessionExpiredError();
};

export const getIdToken = async (options?: {
  forceRefresh?: boolean;
  redirectOnFailure?: boolean;
}): Promise<string | null> => {
  if (!isAuthConfigured) return null;
  const redirectOnFailure = options?.redirectOnFailure ?? true;
  const forceRefresh = options?.forceRefresh ?? false;

  try {
    const token = forceRefresh
      ? await withRefreshNotification(() => fetchUsableIdToken(true))
      : await fetchUsableIdToken(false);
    if (token) return token;
  } catch {
    // Retry below with forceRefresh unless the caller already requested it.
  }

  if (!forceRefresh) {
    try {
      const refreshedToken = await withRefreshNotification(() => fetchUsableIdToken(true));
      if (refreshedToken) return refreshedToken;
    } catch {
      // Fall through to the explicit auth-expired path.
    }
  }

  if (redirectOnFailure) {
    return redirectToLogin();
  }

  throw new AuthSessionExpiredError();
};

export const getAuthUser = async (): Promise<AuthUser | null> => {
  if (!isAuthConfigured) return null;
  try {
    await getIdToken({ redirectOnFailure: false });
    return await getAmplifyCurrentUser();
  } catch {
    return null;
  }
};

export const logout = async (): Promise<void> => {
  if (!isAuthConfigured) return;
  sessionStorage.setItem(manualLogoutKey, 'true');
  sessionStorage.removeItem(authReturnToKey);
  await signOut();
};

export const startTotpSetup = async (): Promise<TotpSetup> => {
  const setupDetails = await setUpTOTP();
  return {
    sharedSecret: setupDetails.sharedSecret,
    setupUri: setupDetails.getSetupUri('NEXT-GIB').toString(),
  };
};

export const verifyTotpSetupCode = async (code: string): Promise<void> => {
  await verifyTOTPSetup({ code });
  await updateMFAPreference({ totp: 'PREFERRED' });
};

export const getAuthRegion = (): string => region;
